'use client'

import React, { startTransition } from 'react'
import {
	Stack,
	Card,
	Typography,
	CardContent,
	Button,
	Box,
} from '@mui/material'
import { AppRoutes } from '@/lib/constants/routes'
import { ExpApiError, formatErrorSupportText } from './lib/errors/apiError'
import {
	AuthenticationError,
	AuthenticationErrorCode,
} from './lib/errors/authError'
import { Strings } from '../lib/constants/strings'
import { ExpApiErrorCode } from './lib/errors/models/errorTypes'
import SupportErrorText from './components/ui/errorComponents/supportErrorText'

interface GlobalErrorProps {
	error: Error | ExpApiError | AuthenticationError
	reset: () => void
}

/**
 * Global error boundary that handles all unhandled errors in the application.
 * Enhanced to provide better error messages for permission-related errors
 * and appropriate recovery options.
 */
export default function GlobalError({
	error,
	reset,
}: Readonly<GlobalErrorProps>) {
	type GlobalErrorContent = {
		image?: string
		primaryLabel: string
		secondaryLabel?: string
		ctaLabel?: string
		onClickBehavior?: () => void
		onTapBehavior?: () => void // kept for compatibility with EmptyStateView
		variant?: 'default' | 'medium'
		errorSupportText?: string[]
	}

	let apiErrorContent: GlobalErrorContent | undefined

	const handleSignIn = () => {
		window.location.href = '/auth/signin'
	}

	const handleGoToSafePage = () => {
		window.location.href = AppRoutes.HOME
	}

	const handleRetry = () => {
		// In global-error.tsx, we can't use useRouter() because there's no router context
		// Instead, we call reset() to attempt recovery, and if that fails, reload the page
		startTransition(() => {
			reset() // clear the error boundary and re-render
			// Note: router.refresh() is not available in global-error context
			// If reset doesn't work, user can reload manually or we could add window.location.reload()
		})
	}

	// customize error content based on instanceof type
	if (error instanceof ExpApiError) {
		const errorCd = error.details?.errorCd
		const reqId = error.details?.reqId

		// Build support lines so GlobalError can render SupportErrorText
		const supportLines =
			errorCd && reqId ? formatErrorSupportText(errorCd, reqId) : undefined

		if (errorCd === ExpApiErrorCode.NETWORK_ERROR) {
			apiErrorContent = {
				primaryLabel: 'Connection Error',
				secondaryLabel: 'Unable to connect...',
				errorSupportText: supportLines,
			}
		} else if (supportLines) {
			// Generic ExpApiError handling surfaces support text when available
			apiErrorContent = {
				primaryLabel: 'API Error',
				secondaryLabel: 'An error occurred while communicating with the API.',
				errorSupportText: supportLines,
			}
		}
	}

	if (error instanceof AuthenticationError) {
		const errorCode = error.details?.errorCode

		if (errorCode === AuthenticationErrorCode.OAUTHCALLBACKERROR) {
			apiErrorContent = {
				primaryLabel: 'Authentication Error',
				secondaryLabel:
					'There was an issue during authentication. Please sign in again.',
				// showSignInOption: true,
				ctaLabel: 'Sign In Again',
				onClickBehavior: handleSignIn,
			}
		}
	}

	// Determine error message and recovery options based on error type
	const getErrorContent = (): GlobalErrorContent => {
		const errorMessage = error.message.toLowerCase()

		if (errorMessage.includes('network') || errorMessage.includes('fetch')) {
			return {
				primaryLabel: 'Connection Error',
				secondaryLabel:
					'Unable to connect to the server. Please check your internet connection.',
			}
		}

		if (errorMessage.includes('timeout')) {
			return {
				primaryLabel: 'Request Timeout',
				secondaryLabel:
					'The request took too long to complete. Please try again.',
			}
		}

		// Default error content
		return {
			primaryLabel: Strings.SOMETHING_WENT_WRONG,
			secondaryLabel:
				'An unexpected error occurred. Please try again or contact support if the problem persists.',
		}
	}

	const computedErrorContent = getErrorContent()
	const errorContent = apiErrorContent ?? computedErrorContent

	return (
		<html lang="en-US">
			<body>
				<main>
					<Stack alignContent="center" mt={6}>
						<Card sx={{ width: '50%', minWidth: '40ch', mx: 'auto', p: 5 }}>
							<CardContent>
								<Stack alignContent="center" spacing={3}>
									<Typography
										variant="h5"
										component="h1"
										sx={{ mx: 'auto', textAlign: 'center' }}
									>
										{errorContent.primaryLabel ?? 'TYPE: Global Error'}
									</Typography>

									<Typography
										variant="body2"
										component="p"
										sx={{ mx: 'auto', textAlign: 'center', maxWidth: '400px' }}
									>
										{errorContent.secondaryLabel ??
											'An unexpected error occurred.'}
									</Typography>

									{/* Error recovery options */}
									<Box
										sx={{
											display: 'flex',
											flexDirection: 'column',
											gap: 2,
											width: '100%',
										}}
									>
										{/* refresh/retry option */}
										<Button
											variant="contained"
											onClick={handleRetry}
											data-testid="global-error-retry"
										>
											Try Again
										</Button>

										{errorContent.ctaLabel && (
											<Button
												variant="outlined"
												onClick={errorContent.onClickBehavior}
												data-testid="global-error-signin"
											>
												{errorContent.ctaLabel}
											</Button>
										)}

										<Button
											variant="text"
											onClick={handleGoToSafePage}
											data-testid="global-error-safe-page"
										>
											Go to Safe Page
										</Button>
										{apiErrorContent?.errorSupportText && (
											<SupportErrorText
												supportLines={apiErrorContent.errorSupportText}
											></SupportErrorText>
										)}
									</Box>

									{/* Error details for debugging (only in development) */}
									{process.env.NODE_ENV === 'development' && (
										<details
											style={{
												marginTop: '20px',
												textAlign: 'left',
												width: '100%',
											}}
										>
											<summary
												style={{ cursor: 'pointer', marginBottom: '10px' }}
											>
												<Typography variant="caption">
													Error Details (Dev Only)
												</Typography>
											</summary>
											<Typography
												variant="caption"
												component="pre"
												sx={{
													whiteSpace: 'pre-wrap',
													fontSize: '10px',
													backgroundColor: 'grey.100',
													padding: 1,
													borderRadius: 1,
													maxHeight: '200px',
													overflow: 'auto',
												}}
											>
												{error.stack ?? error.message}
											</Typography>
										</details>
									)}
								</Stack>
							</CardContent>
						</Card>
					</Stack>
				</main>
			</body>
		</html>
	)
}
